import { floatToDate, getEndOfMonth, getEndOfYear, matchesDate } from '@/parse-and-play/utils.js'

class Filter {
  constructor (appliesTo, type, value) {
    this.appliesTo = appliesTo
    this.type = type
    this.value = value
  }

  toString () {
    return [this.type, `"${this.value}"`].join(':')
  }
}

Filter.APPLIES_TO = {
  TRANSACTION: 'transaction',
  POSTING: 'posting',
  UNKNOWN: 'unknown'
}

const amountFunc = (valueInput) => {
  const initialValue = valueInput.toString().trim()
  const numericIndex = initialValue.search(/[\d.\s<>=]/)

  if (numericIndex === -1) {
    return () => false
  }

  const expression = initialValue.slice(0, numericIndex + 1)
  const value = parseFloat(initialValue.slice(numericIndex + 1).replace(/\s/, ''))

  return (amount) => {
    amount = parseFloat(amount)

    switch (expression) {
      case '>':
        return amount > value
      case '>=':
        return amount >= value
      case '<':
        return amount < value
      case '<=':
        return amount <= value
      case '=':
        return amount === value
      default:
        return false
    }
  }
}

class AmountFilter extends Filter {
  constructor (value) {
    super(Filter.APPLIES_TO.TRANSACTION, 'amount', value)
  }

  filter () {
    // Every transaction has a sum of 0, so this isn't very useful
    // const func = amountFunc(this.value)
    // return (t) => func(t.total)

    return () => false
  }
}

class DateFilter extends Filter {
  constructor (period, value) {
    super(Filter.APPLIES_TO.TRANSACTION, 'date', value)
    this.period = period
  }

  filter () {
    if (this.hasRange) {
      return (transaction) => matchesDate(floatToDate(transaction.date), this.startAt, this.endAt)
    } else {
      return () => true
    }
  }

  get startAt () {
    if (this.hasRange) {
      return this.value
    }

    return null
  }

  get endAt () {
    if (this.period === DateFilter.PERIOD.MONTH) {
      return getEndOfMonth(this.value)
    } else if (this.period === DateFilter.PERIOD.YEAR) {
      return getEndOfYear(this.value)
    }

    return null
  }

  get hasRange () {
    return this.period === DateFilter.PERIOD.MONTH || this.period === DateFilter.PERIOD.YEAR
  }

  toString () {
    const str = ['date', this.period]
    if (this.value) str.push(this.value)
    return str.join(':')
  }
}

DateFilter.PERIOD = {
  MONTH: 'month',
  YEAR: 'year',
  ALL: 'all'
}

DateFilter.thisYear = () => {
  const year = new Date().getFullYear()
  return new DateFilter(DateFilter.PERIOD.YEAR, new Date(year, 0))
}

DateFilter.thisMonth = () => {
  const year = new Date().getFullYear()
  const month = new Date().getMonth()
  return new DateFilter(DateFilter.PERIOD.MONTH, new Date(year, month))
}

DateFilter.all = () => {
  return new DateFilter(DateFilter.PERIOD.ALL)
}

class TypeFilter extends Filter {
  constructor (value) {
    super(Filter.APPLIES_TO.UNKNOWN, 'type', value)
  }
}

class ScenarioFilter extends Filter {
  constructor (value) {
    super(Filter.APPLIES_TO.UNKNOWN, 'scenario', value)
  }
}

class AccountFilter extends Filter {
  constructor (value) {
    super(Filter.APPLIES_TO.POSTING, 'account', value)
  }

  filter () {
    if (this.value.toString().indexOf('*') !== -1) {
      const escaped = this.value.replace(/[.+?^${}()|[\]\\]/g, '\\$&')
      const pattern = escaped.replace(/\*/g, '.*')
      const value = new RegExp('^' + pattern, 'i')

      return (posting) => posting.account.match(value)
    } else {
      return (posting) => posting.account.indexOf(this.value) === 0
    }
  }
}

class BudgetFilter extends Filter {
  constructor (value) {
    super(Filter.APPLIES_TO.TRANSACTION, 'budget', value)
  }

  filter () {
    if (this.value === 'false' || this.value === 'none') {
      return (transaction) => !transaction.periodicName
    } else if (this.value === 'true' || this.value === 'all') {
      return () => true
    } else {
      return (transaction) => !transaction.periodicName || transaction.periodicName === this.value
    }
  }
}

class CurrencyFilter extends Filter {
  constructor (value) {
    super(Filter.APPLIES_TO.POSTING, 'currency', value)
  }

  filter () {
    return (posting) => posting.currency === this.value
  }
}

class PayeeFilter extends Filter {
  constructor (value) {
    super(Filter.APPLIES_TO.TRANSACTION, 'payee', value)
  }

  filter () {
    if (this.value.toString().indexOf('*') !== -1) {
      const escaped = this.value.replace(/[.+?^${}()|[\]\\]/g, '\\$&')
      const pattern = escaped.replace(/\*/g, '.*')
      const value = new RegExp('^' + pattern, 'i')

      return (transaction) => transaction.payee.match(value)
    } else {
      return (transaction) => transaction.payee === this.value
    }
  }
}

class ClearedFilter extends Filter {
  constructor (value) {
    super(Filter.APPLIES_TO.TRANSACTION, 'cleared', value)
  }

  filter () {
    const value = this.value.toString()

    return (transaction) => transaction.cleared.toString() === value
  }
}

class PostingAmountFilter extends Filter {
  constructor (value) {
    super(Filter.APPLIES_TO.POSTING, 'posting.amount', value)
  }

  filter () {
    const func = amountFunc(this.value)

    return (posting) => func(posting.amount)
  }
}

class HasTxnIdFilter extends Filter {
  constructor (value) {
    super(Filter.APPLIES_TO.TRANSACTION, 'hasTxnId', value)
  }

  filter () {
    const value = this.value.toString()

    return (transaction) => (transaction.referenceId ? 'true' : 'false') === value
  }
}

class StagedFilter extends Filter {
  constructor (value) {
    super(Filter.APPLIES_TO.TRANSACTION, 'staged', value)
  }

  filter () {
    const value = this.value.toString() === 'true'

    return (transaction) => transaction.isStaged === value
  }
}

class IgnoredFilter extends Filter {
  constructor (value) {
    super(Filter.APPLIES_TO.TRANSACTION, 'ignored', value)
  }

  filter () {
    const value = this.value.toString() === 'true'

    return (transaction) => transaction.isIgnored === value
  }
}

class SourceFilter extends Filter {
  constructor (value) {
    super(Filter.APPLIES_TO.TRANSACTION, 'source', value)
  }

  filter () {
    return (transaction) => transaction.sourceId === this.value
  }
}

window.Filter = Filter
Filter.fromObj = (obj) => {
  switch (obj.type) {
    case 'account':
      return new AccountFilter(obj.value)
    case 'amount':
      return new AmountFilter(obj.value)
    case 'currency':
      return new CurrencyFilter(obj.value)
    case 'date':
      return new DateFilter(obj.period, new Date(obj.value))
    case 'scenario':
      return new ScenarioFilter(obj.value)
    case 'cleared':
      return new ClearedFilter(obj.value)
    case 'posting.amount':
      return new PostingAmountFilter(obj.value)
    case 'payee':
      return new PayeeFilter(obj.value)
    case 'budget':
      return new BudgetFilter(obj.value)
    case 'hasTxnId':
      return new HasTxnIdFilter(obj.value)
    case 'staged':
      return new StagedFilter(obj.value)
    case 'ignored':
      return new IgnoredFilter(obj.value)
    case 'source':
      return new SourceFilter(obj.value)
    case 'type':
      return new TypeFilter(obj.value)
  }
}

export {
  AmountFilter,
  DateFilter,
  TypeFilter,
  ScenarioFilter,
  AccountFilter,
  CurrencyFilter,
  PayeeFilter,
  ClearedFilter,
  PostingAmountFilter,
  BudgetFilter,
  HasTxnIdFilter,
  StagedFilter,
  IgnoredFilter,
  SourceFilter,
  Filter
}
